/**
 * Module dependencies.
 */

import { Building2, CreditCard, ExternalLink, Search, ShieldCheck, Wallet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from 'app/components/ui/dialog';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bank } from 'app/types/bank-account';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import { Button } from 'app/components/atoms/button/button';
import { CoverflexConnector } from './coverflex-connector';
import { Input } from 'app/components/ui/input';
import { LocalConnector } from './local-connector';
import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { Spinner } from 'app/components/ui/spinner';
import { cn } from 'app/lib/utils';
import { connectBankAccountLocalConnection } from 'app/hooks/requests/bank-accounts/use-bank-account-local-connection';
import { deburr, toLower } from 'lodash';
import { useBankAccountConnectRedirectUrl } from 'app/hooks/requests/bank-accounts/use-bank-account-redirect-url';
import { useBankList } from 'app/hooks/requests/bank-accounts/use-bank-list';
import { useForm } from 'react-hook-form';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  accountId: string | undefined;
  refresh: () => Promise<void>;
  label?: string;
  buttonType?: 'primary' | 'default';
};

/**
 * Wizard steps.
 */

type Step = 'method' | 'bank' | 'credentials' | 'redirect';

/**
 * Open-banking providers (each returns its own bank list).
 */

const openBankingProviders = [
  { key: 'nordigen', label: 'Nordigen' },
  { key: 'enableBanking', label: 'Enable Banking' }
] as const;

/**
 * Synthetic Coverflex "bank" — a local connector reached straight from the
 * method step (no bank grid), identified by name in the backend.
 */

const COVERFLEX_BANK = {
  bic: 'n/a',
  country: 'PT',
  logo: 'https://my.coverflex.com/favicon.ico',
  maximumConsentValidity: '',
  name: 'Coverflex',
  provider: 'local'
} as unknown as Bank;

/**
 * Step indicator.
 */

function Steps({ index }: { index: number }): JSX.Element {
  return (
    <div className={'mb-4 mt-5 flex items-center gap-1.5'}>
      {[0, 1, 2].map(step => (
        <span className={cn('h-1 flex-1 rounded-full', step <= index ? 'bg-primary' : 'bg-muted')} key={step} />
      ))}
    </div>
  );
}

/**
 * Export `ConnectWizard` component.
 *
 * One guided dialog that replaces the old inline "BankList": choose a method,
 * pick a bank, then either enter Edenred credentials (in-app, with the PIN
 * two-step) or get redirected to the bank for Open Banking. On return the app
 * lands on the existing sync page (import date), so that flow is untouched.
 */

export function ConnectWizard({ accountId, refresh, label, buttonType = 'primary' }: Props): JSX.Element {
  const [translate] = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('method');
  const [method, setMethod] = useState<'openBanking' | 'edenred' | 'coverflex'>('openBanking');
  const [provider, setProvider] = useState<string>('nordigen');
  const [search, setSearch] = useState('');
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [pinInfo, setPinInfo] = useState<any>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const client = useRequestProvider();
  const { error: toastError } = useToast();
  const { data: banks, isLoading: isLoadingBanks } = useBankList(provider);
  const { mutateAsync: getRedirectUrl } = useBankAccountConnectRedirectUrl();
  const { control, handleSubmit, reset } = useForm({ mode: 'all' });

  // Feature flags gate which methods/providers are offered.
  const edenredEnabled = useFeature('edenred');
  const coverflexEnabled = useFeature('coverflex');
  const nordigenEnabled = useFeature('nordigen');
  const enableBankingEnabled = useFeature('enableBanking');
  const availableProviders = openBankingProviders.filter(item =>
    item.key === 'nordigen' ? nordigenEnabled : enableBankingEnabled
  );
  const openBankingAvailable = availableProviders.length > 0;
  const defaultProviderKey = availableProviders[0]?.key ?? 'enableBanking';

  // Keep the selected method valid as flags resolve/change.
  useEffect(() => {
    if (method === 'openBanking' && !openBankingAvailable && edenredEnabled) {
      setMethod('edenred');
    } else if (method === 'edenred' && !edenredEnabled && openBankingAvailable) {
      setMethod('openBanking');
    }
  }, [method, openBankingAvailable, edenredEnabled]);

  const stepIndex = step === 'method' ? 0 : step === 'bank' ? 1 : 2;

  const reboot = useCallback(() => {
    setStep('method');
    setMethod('openBanking');
    setProvider('nordigen');
    setSearch('');
    setSelectedBank(null);
    setPinInfo(null);
    setIsConnecting(false);
    setRedirecting(false);
    reset();
  }, [reset]);

  const changeOpen = useCallback(
    (value: boolean) => {
      setOpen(value);

      if (!value) {
        reboot();
      }
    },
    [reboot]
  );

  const filteredBanks = useMemo(() => {
    const term = deburr(toLower(search.trim()));

    if (!term) {
      return banks ?? [];
    }

    return (banks ?? []).filter(bank => deburr(toLower(bank.name)).includes(term));
  }, [banks, search]);

  const pickBank = useCallback(
    async (bank: Bank) => {
      if (!accountId) {
        return;
      }

      if (bank.provider === 'local') {
        setSelectedBank(bank);
        setPinInfo(null);
        reset();
        setStep('credentials');

        return;
      }

      try {
        setSelectedBank(bank);
        setRedirecting(true);

        const result = await getRedirectUrl({ bank, accountId });

        if (result?.url) {
          // Full-page redirect to the bank; the app resumes via wrapper.tsx.
          window.location.href = result.url;

          return;
        }

        setRedirecting(false);
      } catch (error) {
        setRedirecting(false);
      }
    },
    [accountId, getRedirectUrl, reset]
  );

  const onSubmitCredentials = useCallback(
    async (data: any) => {
      if (!selectedBank) {
        return;
      }

      const isCoverflex = selectedBank.name === 'Coverflex';

      if (isCoverflex ? !data.email || !data.password : !data.username || !data.password) {
        return;
      }

      setIsConnecting(true);

      try {
        const result = await connectBankAccountLocalConnection(
          client,
          isCoverflex
            ? {
                bank: selectedBank,
                email: data.email,
                otp: pinInfo ? data.otp : undefined,
                password: data.password
              }
            : {
                bank: selectedBank,
                password: data.password,
                pin: pinInfo ? { ...pinInfo, pin: data.pin } : undefined,
                username: data.username
              }
        );

        if ((result as any)?.status === 'connected') {
          await refresh();

          changeOpen(false);

          return;
        }

        // The bank asked for a PIN — surface the field and wait for a second submit.
        setPinInfo(result);
      } catch (error) {
        // The global handler swallows 401s (to avoid stray toasts on logout), so
        // surface the credential/OTP failure here.
        toastError({
          description: translate('bankAccounts.connect.wizard.credentialsError'),
          message: translate('common.errors.network.default.title')
        });
      } finally {
        setIsConnecting(false);
      }
    },
    [changeOpen, client, pinInfo, refresh, selectedBank, toastError, translate]
  );

  return (
    <Dialog onOpenChange={changeOpen} open={open}>
      <DialogTrigger asChild>
        <Button type={buttonType}>{label ?? translate('bankAccounts.connect.wizard.trigger')}</Button>
      </DialogTrigger>

      <DialogContent className={'sm:max-w-[680px]'}>
        {/* Step 1 — method */}
        {step === 'method' && (
          <>
            <DialogHeader>
              <Steps index={stepIndex} />

              <DialogTitle>{translate('bankAccounts.connect.wizard.methodTitle')}</DialogTitle>

              <DialogDescription>{translate('bankAccounts.connect.wizard.methodSubtitle')}</DialogDescription>
            </DialogHeader>

            <div className={'flex flex-col gap-3'}>
              {openBankingAvailable && (
                <MethodOption
                  active={method === 'openBanking'}
                  description={translate('bankAccounts.connect.wizard.openBankingDescription')}
                  icon={<Building2 className={'h-5 w-5'} />}
                  onClick={() => setMethod('openBanking')}
                  title={translate('bankAccounts.connect.wizard.openBanking')}
                />
              )}

              {edenredEnabled && (
                <MethodOption
                  active={method === 'edenred'}
                  description={translate('bankAccounts.connect.wizard.edenredDescription')}
                  icon={<CreditCard className={'h-5 w-5'} />}
                  onClick={() => setMethod('edenred')}
                  title={translate('bankAccounts.connect.wizard.edenred')}
                />
              )}

              {coverflexEnabled && (
                <MethodOption
                  active={method === 'coverflex'}
                  description={translate('bankAccounts.connect.wizard.coverflexDescription')}
                  icon={<Wallet className={'h-5 w-5'} />}
                  onClick={() => setMethod('coverflex')}
                  title={translate('bankAccounts.connect.wizard.coverflex')}
                />
              )}

              {!openBankingAvailable && !edenredEnabled && !coverflexEnabled && (
                <p className={'py-6 text-center text-sm text-muted-foreground'}>
                  {translate('bankAccounts.connect.wizard.noMethods')}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button onClick={() => changeOpen(false)} type={'default'}>
                {translate('common.actions.cancel')}
              </Button>

              <Button
                disabled={!openBankingAvailable && !edenredEnabled && !coverflexEnabled}
                onClick={() => {
                  if (method === 'coverflex') {
                    setSelectedBank(COVERFLEX_BANK);
                    setPinInfo(null);
                    reset();
                    setStep('credentials');

                    return;
                  }

                  setProvider(method === 'edenred' ? 'local' : defaultProviderKey);
                  setSearch('');
                  setStep('bank');
                }}
                type={'primary'}
              >
                {translate('common.actions.next')}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 2 — choose bank */}
        {step === 'bank' && (
          <>
            <DialogHeader>
              <Steps index={stepIndex} />

              <DialogTitle>{translate('bankAccounts.connect.wizard.bankTitle')}</DialogTitle>

              <DialogDescription>{translate('bankAccounts.connect.wizard.bankSubtitle')}</DialogDescription>
            </DialogHeader>

            {method === 'openBanking' && availableProviders.length > 1 && (
              <div className={'flex gap-2'}>
                {availableProviders.map(item => (
                  <button
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                      provider === item.key
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    )}
                    key={item.key}
                    onClick={() => setProvider(item.key)}
                    type={'button'}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

            <div className={'flex items-center gap-2 rounded-md border border-input px-3'}>
              <Search className={'h-4 w-4 text-muted-foreground'} />

              <Input
                className={'border-0 px-0 shadow-none focus-visible:ring-0'}
                onChange={event => setSearch(event.target.value)}
                placeholder={translate('bankAccounts.connect.wizard.searchPlaceholder')}
                value={search}
              />
            </div>

            <div className={'max-h-[320px] overflow-y-auto'}>
              {isLoadingBanks ? (
                <div className={'grid place-items-center py-10'}>
                  <Spinner />
                </div>
              ) : filteredBanks.length === 0 ? (
                <p className={'py-8 text-center text-sm text-muted-foreground'}>
                  {translate('bankAccounts.connect.wizard.noBanks')}
                </p>
              ) : (
                <div className={'grid grid-cols-3 gap-3'}>
                  {filteredBanks.map(bank => (
                    <button
                      className={
                        'flex flex-col items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:border-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'
                      }
                      disabled={redirecting}
                      key={`${bank.provider}-${bank.name}`}
                      onClick={() => pickBank(bank)}
                      type={'button'}
                    >
                      {bank.logo ? (
                        <img alt={bank.name} className={'h-16 w-full object-contain'} src={bank.logo} />
                      ) : (
                        <div
                          className={
                            'grid h-16 w-full place-items-center rounded-lg bg-primary/10 text-base font-semibold text-primary'
                          }
                        >
                          {bank.name.slice(0, 3).toUpperCase()}
                        </div>
                      )}

                      <span className={'line-clamp-2 text-center text-xs text-muted-foreground'}>{bank.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              {redirecting && (
                <span className={'mr-auto flex items-center gap-2 text-xs text-muted-foreground'}>
                  <Spinner className={'h-4 w-4'} />

                  {translate('bankAccounts.connect.wizard.redirecting')}
                </span>
              )}

              <Button onClick={() => setStep('method')} type={'default'}>
                {translate('common.actions.back')}
              </Button>

              <Button onClick={() => changeOpen(false)} type={'default'}>
                {translate('common.actions.cancel')}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 3 — Edenred / Coverflex credentials */}
        {step === 'credentials' && (
          <>
            <DialogHeader>
              <Steps index={stepIndex} />

              <DialogTitle>{translate('bankAccounts.connect.wizard.credentialsTitle')}</DialogTitle>

              <DialogDescription>
                {selectedBank?.name === 'Coverflex'
                  ? translate('bankAccounts.connect.wizard.coverflexHint')
                  : translate('bankAccounts.connect.wizard.credentialsHint')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmitCredentials)}>
              {selectedBank?.logo && (
                <img alt={selectedBank.name} className={'mx-auto mb-2 h-10 object-contain'} src={selectedBank.logo} />
              )}

              {selectedBank?.name === 'Coverflex' ? (
                <CoverflexConnector control={control as any} showOtp={!!pinInfo} />
              ) : (
                <LocalConnector control={control as any} showPin={!!pinInfo} />
              )}

              <div
                className={
                  'mt-4 flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground'
                }
              >
                <ShieldCheck className={'mt-0.5 h-4 w-4 shrink-0'} />

                <span>
                  {selectedBank?.name === 'Coverflex'
                    ? translate('bankAccounts.connect.wizard.coverflexSafety')
                    : translate('bankAccounts.connect.wizard.credentialsSafety')}
                </span>
              </div>

              <DialogFooter className={'mt-4'}>
                <Button
                  onClick={() => {
                    setPinInfo(null);
                    reset();
                    setStep(selectedBank?.name === 'Coverflex' ? 'method' : 'bank');
                  }}
                  type={'default'}
                >
                  {translate('common.actions.back')}
                </Button>

                <Button onClick={() => changeOpen(false)} type={'default'}>
                  {translate('common.actions.cancel')}
                </Button>

                <SubmitButton
                  className={'mt-0'}
                  label={translate(
                    selectedBank?.name === 'Coverflex'
                      ? 'bankAccounts.connect.wizard.linkAccount'
                      : 'bankAccounts.connect.wizard.linkCard'
                  )}
                  loading={isConnecting}
                />
              </DialogFooter>
            </form>
          </>
        )}

        {/* Redirect fallback (only if a redirect is in-flight without leaving yet) */}
        {step === 'redirect' && (
          <div className={'flex items-center gap-3 py-6'}>
            <ExternalLink className={'h-5 w-5 text-primary'} />

            <p className={'text-sm text-muted-foreground'}>{translate('bankAccounts.connect.wizard.redirectNote')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * `MethodOption` — a selectable method card.
 */

function MethodOption({
  active,
  description,
  icon,
  onClick,
  title
}: {
  active: boolean;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  title: string;
}): JSX.Element {
  return (
    <button
      className={cn(
        'flex items-start gap-3 rounded-md border p-3.5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-border hover:border-primary/50 hover:bg-primary/[0.03]'
      )}
      onClick={onClick}
      type={'button'}
    >
      <div
        className={cn(
          'grid h-10 w-10 shrink-0 place-items-center rounded-lg',
          active ? 'bg-primary text-primary-foreground' : 'bg-muted text-primary'
        )}
      >
        {icon}
      </div>

      <div>
        <div className={'text-sm font-semibold'}>{title}</div>

        <p className={'mt-0.5 text-xs leading-relaxed text-muted-foreground'}>{description}</p>
      </div>
    </button>
  );
}
